// item-filter — small pure helpers that filter IngestPackage item arrays
// by chapter (caseId), topic (topicId or subtopicId), or kind.
//
// 19AW Phase 3: extracted into one place so quiz-view, flashcards-view, and
// the upcoming /drill route filter using identical semantics. The
// "subtopicId is a more specific anchor; if filtering by topicId both
// topicId and subtopicId matches count" rule is the primary trap we lock
// down — getting it wrong causes per-topic practice to silently miss the
// items closest to the topic.
//
// Empty/null id semantics: items with `topicId === null` or `topicId
// undefined` are NOT included in topic-filtered results. They surface only
// in chapter-level filters or unfiltered queues.

export interface TopicAware {
  caseId?: string | null;
  topicId?: string | null;
  subtopicId?: string | null;
}

/** Filter by topic id. An item matches when its topicId OR subtopicId
 *  equals the requested id. null/undefined ids never match. */
export function filterByTopic<T extends TopicAware>(items: readonly T[], topicId: string): T[] {
  if (!topicId) return [];
  return items.filter((it) => {
    const t = it.topicId;
    const s = it.subtopicId;
    return (typeof t === 'string' && t === topicId)
        || (typeof s === 'string' && s === topicId);
  });
}

/** Filter by chapter id (caseId). null/undefined caseId never matches. */
export function filterByChapter<T extends TopicAware>(items: readonly T[], caseId: string): T[] {
  if (!caseId) return [];
  return items.filter((it) => typeof it.caseId === 'string' && it.caseId === caseId);
}

/** Compose: chapter then topic. Useful when both are present in the URL —
 *  topic narrows further inside the chapter (no-op if topicId is empty). */
export function filterByChapterAndTopic<T extends TopicAware>(
  items: readonly T[],
  caseId: string | null,
  topicId: string | null,
): T[] {
  let result: readonly T[] = items;
  if (caseId) result = filterByChapter(result, caseId);
  if (topicId) result = filterByTopic(result, topicId);
  return result as T[];
}
